'use client';

/**
 * RecordCard Component
 *
 * Displays an entity record in a card format with field summaries,
 * status indicators, timestamps, and action buttons.
 */

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { createNeonGlow, glassEffects } from '../../theme/shadows';
import { interactionAnimations, motionPresets, transitionPresets } from '../../theme/transitions';
import type { EntityDefinition, EntityRecord, FieldDefinition } from '../../types/entityFramework';
import { formatRelativeTime } from '../../utils/formatters';
import { GlassTooltip } from '../GlassTooltip';
import { StatusDot } from '../StatusDot';

// ============================================================================
// Types
// ============================================================================

export interface RecordCardProps {
  /** The entity record to display */
  record: EntityRecord;
  /** The entity definition (schema) */
  entity: EntityDefinition;
  /** Field names to display (defaults to first 3 non-system fields) */
  displayFields?: string[];
  /** Card variant */
  variant?: 'compact' | 'default' | 'detailed';
  /** Click handler for viewing record details */
  onClick?: (record: EntityRecord) => void;
  /** Edit action handler */
  onEdit?: (record: EntityRecord) => void;
  /** Delete action handler */
  onDelete?: (record: EntityRecord) => void;
  /** Duplicate action handler */
  onDuplicate?: (record: EntityRecord) => void;
  /** Whether to show action buttons */
  showActions?: boolean;
  /** Whether to show timestamps */
  showTimestamps?: boolean;
  /** Whether this card is selected */
  selected?: boolean;
  /** Neon accent color for glow effects */
  neonAccent?: NeonAccent;
  /** Additional CSS class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledCardProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $selected: boolean;
  $clickable: boolean;
  $variant: 'compact' | 'default' | 'detailed';
}

const StyledCard = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledCardProps>(({ theme, $isDark, $neonAccent, $selected, $clickable, $variant }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  const getPadding = () => {
    switch ($variant) {
      case 'compact':
        return theme.spacing(1.5, 2);
      case 'detailed':
        return theme.spacing(3);
      default:
        return theme.spacing(2, 2.5);
    }
  };

  return {
    position: 'relative',
    borderRadius: $variant === 'compact' ? 12 : 16,
    overflow: 'hidden',
    transition: transitionPresets.card,
    cursor: $clickable ? 'pointer' : 'default',
    padding: getPadding(),

    // Selection ring
    ...($selected && {
      outline: `2px solid ${neonStyles?.base || theme.palette.primary.main}`,
      outlineOffset: 2,
    }),

    // Use neon styling if accent is set, otherwise use glass styling
    ...(neonStyles
      ? {
          background: neonStyles.background,
          border: `1px solid ${neonStyles.border}`,
          boxShadow: createNeonGlow($neonAccent!, 'subtle'),

          '&:hover': {
            boxShadow: createNeonGlow($neonAccent!, 'medium'),
          },
        }
      : {
          background: glass.background,
          backdropFilter: glass.backdropBlur,
          border: `1px solid ${glass.border}`,
          boxShadow: glass.shadow,

          '&:hover': {
            background: glass.backgroundHover,
            border: `1px solid ${glass.borderHover}`,
            boxShadow: glass.shadowHover,
          },
        }),
  };
});

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
}));

const StyledFieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 2,
}));

const StyledFieldValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
}));

const StyledTimestamp = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}));

interface StyledActionButtonProps {
  $isDark: boolean;
  $variant: 'edit' | 'delete' | 'duplicate';
}

const StyledActionButton = styled(IconButton, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledActionButtonProps>(({ theme, $isDark, $variant }) => {
  const getHoverColor = () => {
    switch ($variant) {
      case 'edit':
        return $isDark ? 'rgba(14, 165, 233, 0.15)' : 'rgba(14, 165, 233, 0.1)';
      case 'delete':
        return $isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)';
      case 'duplicate':
        return $isDark ? 'rgba(139, 92, 246, 0.15)' : 'rgba(139, 92, 246, 0.1)';
      default:
        return theme.palette.action.hover;
    }
  };

  const getColor = () => {
    switch ($variant) {
      case 'edit':
        return '#0ea5e9';
      case 'delete':
        return '#ef4444';
      case 'duplicate':
        return '#8b5cf6';
      default:
        return theme.palette.text.secondary;
    }
  };

  return {
    width: 32,
    height: 32,
    color: theme.palette.text.secondary,
    transition: transitionPresets.all,

    '&:hover': {
      background: getHoverColor(),
      color: getColor(),
    },
  };
});

// ============================================================================
// Icons (inline SVG for better bundle size)
// ============================================================================

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M11.5 2.5l2 2-8 8H3.5v-2l8-8z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M3 4.5h10M6.5 4.5v-1a1 1 0 011-1h1a1 1 0 011 1v1M12.5 4.5v8a1 1 0 01-1 1h-7a1 1 0 01-1-1v-8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DuplicateIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="5.5" y="5.5" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M10.5 5.5v-2a1 1 0 00-1-1h-6a1 1 0 00-1 1v6a1 1 0 001 1h2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ExpandIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="currentColor"
    style={{
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
    }}
  >
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
  </svg>
);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the record title from the primary field or ID
 */
function getRecordTitle(record: EntityRecord, entity: EntityDefinition): string {
  const data = record.data;

  // Try to find a primary/name field
  const primaryFieldCandidates = ['name', 'title', 'displayName', 'fullName', 'label', 'subject', 'email'];

  for (const fieldName of primaryFieldCandidates) {
    const value = data[fieldName];
    if (value && typeof value === 'string') {
      return value;
    }
  }

  // Try the first text field in the entity definition
  const firstTextField = entity.fields.find((f) => ['TEXT', 'TEXTAREA', 'EMAIL'].includes(f.fieldType));
  if (firstTextField && data[firstTextField.name]) {
    return String(data[firstTextField.name]);
  }

  // Fallback to ID
  return `${entity.label} #${record.id.slice(0, 8)}`;
}

/**
 * Get default display fields if not provided
 */
function getDefaultDisplayFields(entity: EntityDefinition, variant: 'compact' | 'default' | 'detailed'): string[] {
  const limit = variant === 'compact' ? 2 : variant === 'detailed' ? 6 : 3;

  // Exclude primary name fields and system fields
  const excludeFields = new Set([
    'name',
    'title',
    'displayName',
    'fullName',
    'label',
    'id',
    'createdAt',
    'updatedAt',
    'createdBy',
    'updatedBy',
  ]);

  return entity.fields
    .filter((f) => !excludeFields.has(f.name))
    .slice(0, limit)
    .map((f) => f.name);
}

/**
 * Format a field value for display
 */
function formatFieldValue(value: unknown, field: FieldDefinition): string {
  if (value == null) return '-';

  switch (field.fieldType) {
    case 'BOOLEAN':
      return value ? 'Yes' : 'No';

    case 'DATE':
    case 'DATETIME':
      return formatRelativeTime(value as Date | string);

    case 'CURRENCY':
      if (typeof value === 'number') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: field.currency || 'USD',
        }).format(value);
      }
      return String(value);

    case 'PERCENTAGE':
      if (typeof value === 'number') {
        return `${value}%`;
      }
      return String(value);

    case 'SELECT':
      if (field.options) {
        const option = field.options.find((o) => o.value === value);
        return option?.label || String(value);
      }
      return String(value);

    case 'MULTI_SELECT':
      if (Array.isArray(value) && field.options) {
        return value.map((v) => field.options?.find((o) => o.value === v)?.label || v).join(', ');
      }
      return String(value);

    case 'ADDRESS':
      if (typeof value === 'object' && value !== null) {
        const addr = value as Record<string, string>;
        return [addr['city'], addr['state'], addr['country']].filter(Boolean).join(', ');
      }
      return String(value);

    default:
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return String(value);
  }
}

/**
 * Get status color based on record data
 */
function getStatusColor(record: EntityRecord): 'success' | 'warning' | 'error' | 'info' | 'neutral' {
  const status = record.data['status'];
  if (!status) return 'neutral';

  const statusStr = String(status).toLowerCase();

  if (statusStr.includes('active') || statusStr.includes('complete') || statusStr.includes('approved')) {
    return 'success';
  }

  if (statusStr.includes('pending') || statusStr.includes('review') || statusStr.includes('draft')) {
    return 'warning';
  }

  if (statusStr.includes('error') || statusStr.includes('reject') || statusStr.includes('cancel')) {
    return 'error';
  }

  if (statusStr.includes('new') || statusStr.includes('open')) {
    return 'info';
  }

  return 'neutral';
}

// ============================================================================
// Component
// ============================================================================

export const RecordCard = forwardRef<HTMLDivElement, RecordCardProps>(
  (
    {
      record,
      entity,
      displayFields,
      variant = 'default',
      onClick,
      onEdit,
      onDelete,
      onDuplicate,
      showActions = true,
      showTimestamps = true,
      selected = false,
      neonAccent,
      className,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [expanded, setExpanded] = useState(variant === 'detailed');

    // Get record title
    const title = useMemo(() => getRecordTitle(record, entity), [record, entity]);

    // Get fields to display
    const fieldsToShow = useMemo(() => {
      const fieldNames = displayFields || getDefaultDisplayFields(entity, variant);
      return fieldNames
        .map((name) => entity.fields.find((f) => f.name === name))
        .filter((f): f is FieldDefinition => f !== undefined);
    }, [displayFields, entity, variant]);

    // Get status color
    const statusColor = useMemo(() => getStatusColor(record), [record]);

    // Handlers
    const handleClick = useCallback(() => {
      onClick?.(record);
    }, [onClick, record]);

    const handleEdit = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onEdit?.(record);
      },
      [onEdit, record],
    );

    const handleDelete = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete?.(record);
      },
      [onDelete, record],
    );

    const handleDuplicate = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onDuplicate?.(record);
      },
      [onDuplicate, record],
    );

    const toggleExpanded = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setExpanded((prev) => !prev);
    }, []);

    const hasActions = showActions && (onEdit || onDelete || onDuplicate);

    return (
      <StyledCard
        ref={ref}
        className={className}
        $isDark={isDark}
        $neonAccent={neonAccent}
        $selected={selected}
        $clickable={!!onClick}
        $variant={variant}
        onClick={onClick ? handleClick : undefined}
        initial={motionPresets.fadeInUp.initial}
        animate={motionPresets.fadeInUp.animate}
        transition={motionPresets.fadeInUp.transition}
        whileHover={onClick ? interactionAnimations.cardHover : undefined}
      >
        {/* Header Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: variant === 'compact' ? 0 : 1.5,
          }}
        >
          {/* Title and Status */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {record.data['status'] != null && (
              <StatusDot
                color={statusColor}
                size={variant === 'compact' ? 'small' : 'medium'}
                tooltip={String(record.data['status'])}
              />
            )}
            <StyledTitle
              sx={{
                fontSize: variant === 'compact' ? '0.875rem' : '1rem',
              }}
            >
              {title}
            </StyledTitle>
          </Box>

          {/* Actions */}
          {hasActions && (
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              {onEdit && (
                <GlassTooltip title="Edit">
                  <StyledActionButton $isDark={isDark} $variant="edit" onClick={handleEdit} size="small">
                    <EditIcon />
                  </StyledActionButton>
                </GlassTooltip>
              )}
              {onDuplicate && (
                <GlassTooltip title="Duplicate">
                  <StyledActionButton $isDark={isDark} $variant="duplicate" onClick={handleDuplicate} size="small">
                    <DuplicateIcon />
                  </StyledActionButton>
                </GlassTooltip>
              )}
              {onDelete && (
                <GlassTooltip title="Delete">
                  <StyledActionButton $isDark={isDark} $variant="delete" onClick={handleDelete} size="small">
                    <DeleteIcon />
                  </StyledActionButton>
                </GlassTooltip>
              )}
            </Stack>
          )}

          {/* Expand button for detailed variant */}
          {variant === 'detailed' && (
            <IconButton
              onClick={toggleExpanded}
              size="small"
              sx={{
                width: 28,
                height: 28,
                color: 'text.secondary',
              }}
            >
              <ExpandIcon expanded={expanded} />
            </IconButton>
          )}
        </Box>

        {/* Field Summary */}
        {variant !== 'compact' && (
          <Box sx={{ mb: showTimestamps ? 1.5 : 0 }}>
            <Stack
              direction="row"
              spacing={3}
              sx={{
                flexWrap: 'wrap',
                '& > *': {
                  minWidth: 80,
                  maxWidth: variant === 'detailed' ? 'none' : 150,
                },
              }}
            >
              {fieldsToShow.slice(0, variant === 'detailed' && !expanded ? 3 : undefined).map((field) => (
                <Box key={field.name} sx={{ flex: variant === 'detailed' ? '0 0 auto' : 1 }}>
                  <StyledFieldLabel>{field.label}</StyledFieldLabel>
                  <StyledFieldValue>{formatFieldValue(record.data[field.name], field)}</StyledFieldValue>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Expanded Details (for detailed variant) */}
        {variant === 'detailed' && (
          <Collapse in={expanded}>
            <Box
              sx={{
                pt: 1.5,
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              }}
            >
              <Stack spacing={1.5}>
                {fieldsToShow.slice(3).map((field) => (
                  <Box key={field.name}>
                    <StyledFieldLabel>{field.label}</StyledFieldLabel>
                    <StyledFieldValue sx={{ whiteSpace: 'normal' }}>
                      {formatFieldValue(record.data[field.name], field)}
                    </StyledFieldValue>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Collapse>
        )}

        {/* Timestamps */}
        {showTimestamps && variant !== 'compact' && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pt: 1.5,
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            }}
          >
            <StyledTimestamp>Created {formatRelativeTime(record.createdAt)}</StyledTimestamp>
            {record.updatedAt && record.updatedAt !== record.createdAt && (
              <StyledTimestamp>Updated {formatRelativeTime(record.updatedAt)}</StyledTimestamp>
            )}
          </Box>
        )}
      </StyledCard>
    );
  },
);

RecordCard.displayName = 'RecordCard';

export default RecordCard;
